// @flow
import * as React from 'react';
import {
  Avatar,
  Button,
  Checkbox, CircularProgress,
  FormControl,
  FormControlLabel,
  FormHelperText,
  MenuItem,
  Stack
} from '@mui/material';
import { FormikSelectInput, FormikTextInput, PhoneNumberInput } from '@coverright/ui/inputs';
import { LoadingButton } from '@mui/lab';
import { useFormik } from 'formik';
import * as yup from 'yup';
import { useSaveAgentMutation, useUploadAgentAvatarMutation } from '../shared';
import { enqueueSnackbar } from 'notistack';
import { uploadClient } from '@coverright/data-access/apollo-clients';
import { useRef, useState } from 'react';
import { AgentInput, AgentRoleInput } from '@coverright/data-access/types/enrollment';
import { validatePhone } from '@coverright/features';
import { getIsAgentManager, keycloak, KeycloakRoles } from '@coverright/shared/keycloak';
import { environment } from '@coverright/shared/config';

const validationSchema = yup.object({
  firstName: yup
    .string()
    .required('First name is required'),
  lastName: yup
    .string()
    .required('Last name is required'),
  email: yup
    .string()
    .email('Enter a valid email')
    .required('Email is required'),
  phone: yup
    .string()
    .test('test-phone', 'Please enter valid phone number', validatePhone)
  ,
  consultationUrl: yup.string(),
  enrollmentMeetingUrl: yup.string(),
  b2bMeetingUrl: yup.string(),
  planReviewUrl: yup.string(),
  picUrl: yup.string(),
  enabled: yup.boolean(),
  receiveEmails: yup.boolean(),
  enabledForRoundRobin: yup.boolean().nullable(),
  role: yup.string().oneOf(Object.values(AgentRoleInput)).required('Role is required'),
});

type Props = {
  input?: AgentInput,
  onSubmit: (data?: AgentInput) => void,
  hideCancelButton?: boolean
};
export const AgentForm = ({input, onSubmit, hideCancelButton}: Props) => {
  const isAgentManager = getIsAgentManager(keycloak);
  const [picUrl, setPicUrl] = useState(input?.picUrl);
  const ref = useRef<any>();
  const [upload, {loading: uploadLoading}] = useUploadAgentAvatarMutation({
    client: uploadClient,
    onCompleted: res => {
      setPicUrl(res.uploadAgentAvatar)
      enqueueSnackbar('File uploaded! Don\'t forget to save form!' , {variant: "info"});
    }
  })

  const formik = useFormik({
    initialValues: {
      firstName: input?.firstName || '',
      lastName: input?.lastName || '',
      email: input?.email || '',
      consultationUrl: input?.consultationUrl || '',
      enrollmentMeetingUrl: input?.enrollmentMeetingUrl || '',
      planReviewUrl: input?.planReviewUrl || '',
      b2bMeetingUrl: input?.b2bMeetingUrl || '',
      phone: input?.phone || '',
      receiveEmails: input ? input.receiveEmails : true,
      enabled: input ? input.enabled : true,
      role: input?.role || '',
      enabledForRoundRobin: input ? input.enabledForRoundRobin : false,
    },
    validationSchema: validationSchema,
    onSubmit: async (data?: AgentInput) => {
      save({
        variables: {
          input: {
            id: input?.id,
            ...data,
            picUrl
          }
        }
      })
    },
  });

  const [save, {loading}] = useSaveAgentMutation({
    onCompleted: () => {
      enqueueSnackbar('Changes saved', {variant: 'success'});
      onSubmit && onSubmit();
    },
    refetchQueries: ['agents'],
    update: (cache) => {
      if (input) {
        cache.modify({
          id: cache.identify(input),
          fields(cached, { fieldName,  }) {
            if (fieldName === 'picUrl') {
              return picUrl || cached;
            }
            return formik.values[fieldName] || cached;
          }
        })
      }
    }
  })

  const onUpload = async (file: any) => {
    await upload({variables: {img: file}});
    if (ref?.current) {
      ref.current.value = '';
    }
  }

  return (
    <Stack component={'form'} gap={2} onSubmit={formik.handleSubmit}>
      <Stack gap={2} direction={'row'} alignItems={'center'}>
        <FormikTextInput formik={formik} name={'firstName'} required />
        <FormikTextInput formik={formik} name={'lastName'} required />
      </Stack>

      <Stack gap={2} direction={'row'} alignItems={'center'}>
        <FormikTextInput formik={formik} name={'email'} required />
        <PhoneNumberInput value={formik.values.phone}
                          label={'Phone number'}
                          error={formik.touched.phone && Boolean(formik.errors.phone)}
                          helperText={formik.touched.phone && formik.errors.phone as string}
                          placeholder={'+1 (000) 000-0000'}
                          onChange={e => (
                            formik.setFieldValue('phone', e.target.value)
                          )} />
      </Stack>

      {!!isAgentManager && <FormikSelectInput formik={formik}
                         name={'role'}
                         label={'Role'}
                         hideTick
      >
        {roles.map(role => (
          <MenuItem value={role} key={role}>{roleLabel(role)}</MenuItem>
        ))}
      </FormikSelectInput>}

      <FormikTextInput formik={formik} label={'Calendly enrollment meeting url'} name={'enrollmentMeetingUrl'} />
      <FormikTextInput formik={formik} label={'Calendly plan review meeting url'} name={'planReviewUrl'} />
      <FormikTextInput formik={formik} label={'B2B meeting url'} name={'b2bMeetingUrl'} />
      <FormikTextInput formik={formik} label={'Consultation url'} name={'consultationUrl'} />


      {!!isAgentManager && <Stack gap={2} direction={'row'} alignItems={'center'}>
        <FormControl error={formik.touched.enabled && Boolean(formik.errors.enabled)}>
          <FormControlLabel
            control={
              <Checkbox
                checked={formik.values.enabled ?? false}
                onChange={e => {
                  formik.setFieldValue('enabled', e.target.checked);
                }}
                color="primary"
              />
            }
            label="Enabled"
          />
          {(formik.touched.enabled && formik.errors.enabled) &&
            <FormHelperText>{formik.errors.enabled as string}</FormHelperText>}
        </FormControl>
        <FormControl className={'ml-5'} error={formik.touched.receiveEmails && Boolean(formik.errors.receiveEmails)}>
          <FormControlLabel
            control={
              <Checkbox
                checked={formik.values.receiveEmails ?? false}
                onChange={e => {
                  formik.setFieldValue('receiveEmails', e.target.checked);
                }}
                color="primary"
              />
            }
            label="Receive emails"
          />
          {(formik.touched.receiveEmails && formik.errors.receiveEmails) &&
            <FormHelperText>{formik.errors.receiveEmails as string}</FormHelperText>}
        </FormControl>
        <FormControl className={'ml-5'}
                     error={formik.touched.enabledForRoundRobin && Boolean(formik.errors.enabledForRoundRobin)}>
          <FormControlLabel
            control={
              <Checkbox
                checked={formik.values.enabledForRoundRobin ?? false}
                onChange={e => {
                  formik.setFieldValue('enabledForRoundRobin', e.target.checked);
                }}
                color="primary"
              />
            }
            label="In RR"
          />
          {(formik.touched.enabledForRoundRobin && formik.errors.enabledForRoundRobin) &&
            <FormHelperText>{formik.errors.enabledForRoundRobin as string}</FormHelperText>}
        </FormControl>
      </Stack>}

      <Stack gap={2} direction={'row'} alignItems={'center'} justifyContent={'space-between'}>
        <Stack gap={2} direction={'row'} alignItems={'center'} flex={1}>
          <Avatar sx={{width: 40, height: 40, bgcolor: '#2d969f'}}
                  src={picUrl ? environment.cdnUrl + picUrl.replace(environment.cdnUrl, '') : undefined} />
          {!uploadLoading && <>
            <input ref={ref} accept="image/png, image/gif, image/jpeg"
                   onChange={(e: React.ChangeEvent<HTMLInputElement>) => onUpload((e?.target?.files as any)[0])}
                   type="file" />
          </>}
          {uploadLoading && <CircularProgress />}
        </Stack>
        {!hideCancelButton && (
          <Button onClick={() => onSubmit()} variant={'outlined'}>
            Cancel
          </Button>
        )}
        <LoadingButton
          loading={loading}
          type="submit"
          variant={'contained'}
        >
          Save
        </LoadingButton>
      </Stack>

    </Stack>
  );
};

const roles = [
  AgentRoleInput.RoleAdmin,
  AgentRoleInput.RoleAgentManager,
  AgentRoleInput.RoleCsr,
  AgentRoleInput.RoleSuperAdmin,
  AgentRoleInput.RoleTechStaff,
];

const roleLabel = (role: AgentRoleInput) => {
  switch (role) {
    case AgentRoleInput.RoleAdmin: return 'Agent';
    case AgentRoleInput.RoleAgentManager: return 'Agent Manager';
    case AgentRoleInput.RoleCsr: return 'CSR';
    case AgentRoleInput.RoleSuperAdmin: return 'Super Admin';
    case AgentRoleInput.RoleTechStaff: return 'Tech Staff';
  }
};
